import { createPortal } from "react-dom";
import { Logo } from "../Logo";
import type { Integration } from "../../../types/integration";
import { useAnchoredMenu } from "./useAnchoredMenu";
import styles from "./ConnectChoiceMenu.module.css";

export type ConnectChoice = {
  slug: string;
  title: string;
  integration: Integration;
};

type Props = {
  // What the customer is choosing, e.g. "Connect your email". Names the menu.
  prompt: string;
  choices: ReadonlyArray<ConnectChoice>;
  busySlug: string | null;
  onChoose: (choice: ConnectChoice) => void;
};

// One "Connect ▾" for a requirement any of several providers satisfies, so the
// row reads like every single-app row beside it. The list is portalled with
// fixed coordinates, because the checklist and the hero both clip their
// overflow, into the dashboard's modal host so it keeps the `--ds-*` tokens.
export const ConnectChoiceMenu = ({ prompt, choices, busySlug, onChoose }: Props): JSX.Element => {
  const menu = useAnchoredMenu();
  const busy = busySlug !== null && choices.some((c) => c.slug === busySlug);
  const menuId = `connect-choice-${prompt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <>
      <button
        ref={menu.triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-controls={menu.open ? menuId : undefined}
        aria-label={`${prompt}: choose a provider`}
        disabled={busy}
        onClick={menu.toggle}
      >
        {busy ? "Opening…" : "Connect"}
        <span className={styles.chevron} aria-hidden="true">
          ▾
        </span>
      </button>
      {menu.open
        ? createPortal(
            <div
              ref={menu.menuRef}
              id={menuId}
              role="menu"
              aria-label={prompt}
              className={styles.menu}
              style={menu.menuStyle}
              onKeyDown={menu.onMenuKeyDown}
            >
              {choices.map((choice, i) => (
                <ChoiceItem
                  key={choice.slug}
                  choice={choice}
                  itemRef={menu.setItemRef(i)}
                  onClick={() => {
                    menu.close(false);
                    onChoose(choice);
                  }}
                />
              ))}
            </div>,
            document.querySelector<HTMLElement>("[data-modal-host]") ?? document.body,
          )
        : null}
    </>
  );
};

type ChoiceItemProps = {
  choice: ConnectChoice;
  itemRef: (el: HTMLButtonElement | null) => void;
  onClick: () => void;
};

const ChoiceItem = ({ choice, itemRef, onClick }: ChoiceItemProps): JSX.Element => (
  <button ref={itemRef} type="button" role="menuitem" className={styles.item} onClick={onClick}>
    <Logo
      slug={choice.slug}
      size={20}
      brandHex={choice.integration.iconBrandHex ?? null}
      logoUrl={choice.integration.ourLogoUrl ?? choice.integration.logoUrl ?? null}
    />
    <span>{choice.title}</span>
  </button>
);
